// SCR-CONSOLE-005 — صدور لایسنس On-prem (US-PLT-040، AC-PLT-040.1): تاریخچه لایسنس‌های صادرشده با وضعیت ابطال،
// صدور فایل امضاشده (مشتری، شناسه نصب، پلن و نسخه آن، افزونه‌ها، محدودیت‌ها، بازه اعتبار و بازه نسخه مجاز)، دانلود
// مجدد فایل، ابطال دستی و کلید عمومی امضا برای تعبیه در Image نصب مشتری (ADR-008).
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type IssuedLicenseView, type PlanVersionView } from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  StatusChip,
  TextFieldRHF,
  zBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { consoleKeys, publishedVersions, toNumber } from '../../api';

export const SCR_CONSOLE_005 = 'SCR-CONSOLE-005';

const issueSchema = z.object({
  customerId: z.string().trim().min(1).max(64),
  customerName: z.string().trim().min(1).max(200),
  installationId: z.string().trim().min(1).max(64),
  planCode: z.string().trim().min(1),
  validFrom: zBusinessDate(),
  validUntil: zBusinessDate(),
  minVersion: z.string().trim().max(32).optional(),
  maxVersion: z.string().trim().max(32).optional(),
});
type IssueForm = z.input<typeof issueSchema>;

/** دانلود متن فایل در مرورگر (بدون نگه‌داشتن کلید یا مفاد در Cache). */
function download(fileName: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function IssueDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();

  const plans = useQuery({
    queryKey: consoleKeys.plans,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/plans')),
    enabled: open,
  });

  const { control, handleSubmit, formState, reset, setValue, watch } = useForm<IssueForm>({
    resolver: zodResolver(issueSchema),
    defaultValues: {
      customerId: '',
      customerName: '',
      installationId: '',
      planCode: '',
      validFrom: undefined as unknown as string,
      validUntil: undefined as unknown as string,
      minVersion: '',
      maxVersion: '',
    },
  });

  const planCode = watch('planCode');
  const selected = useMemo<PlanVersionView | undefined>(() => {
    const plan = (plans.data?.items ?? []).find((item) => item.code === planCode);
    if (!plan) {
      return undefined;
    }
    return publishedVersions(plan).sort((a, b) => (toNumber(b.version) ?? 0) - (toNumber(a.version) ?? 0))[0];
  }, [plans.data, planCode]);

  const issue = useMutation({
    mutationFn: (values: IssueForm) =>
      unwrap(
        api.POST('/api/v1/platform/console/licenses', {
          body: {
            customerId: values.customerId.trim(),
            customerName: values.customerName.trim(),
            installationId: values.installationId.trim(),
            plan: values.planCode,
            addons: [],
            features: selected?.features ?? [],
            limits: selected?.limits ?? {},
            validFrom: values.validFrom,
            validUntil: values.validUntil,
            minVersion: values.minVersion?.trim() || null,
            maxVersion: values.maxVersion?.trim() || null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (data) => {
      download(data.fileName, data.content);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.licensesRoot });
      close();
    },
  });

  function close() {
    reset();
    issue.reset();
    onClose();
  }

  const submit = handleSubmit((values) => issue.mutate(values));

  return (
    <FormDialog
      open={open}
      titleKey="console:licenses.issue.title"
      onSubmit={() => void submit()}
      onClose={close}
      isDirty={formState.isDirty}
      isSubmitting={issue.isPending}
      submitError={issue.error}
      submitKey="console:licenses.issue.submit"
      maxWidth="md"
    >
      <TextFieldRHF
        control={control}
        name="customerId"
        labelKey="console:licenses.fields.customer_id"
        inputProps={{ dir: 'ltr' }}
      />
      <TextFieldRHF control={control} name="customerName" labelKey="console:licenses.fields.customer_name" />
      <TextFieldRHF
        control={control}
        name="installationId"
        labelKey="console:licenses.fields.installation_id"
        helperKey="console:licenses.fields.installation_help"
        inputProps={{ dir: 'ltr' }}
      />
      <TextField
        select
        label={t('console:licenses.fields.plan')}
        value={planCode}
        onChange={(event) => setValue('planCode', event.target.value, { shouldDirty: true })}
        helperText={t('console:licenses.fields.plan_help')}
        data-testid="license-plan"
        fullWidth
      >
        {(plans.data?.items ?? [])
          .filter((plan) => publishedVersions(plan).length > 0)
          .map((plan) => (
            <MenuItem key={plan.code} value={plan.code}>
              {plan.code}
            </MenuItem>
          ))}
      </TextField>
      {selected && (
        <Alert severity="info" data-testid="license-plan-summary">
          {t('console:licenses.issue.plan_summary', {
            version: faDigits(toNumber(selected.version) ?? 0),
            features: faDigits(selected.features.length),
            limits: faDigits(Object.keys(selected.limits).length),
          })}
        </Alert>
      )}
      <JalaliDatePickerRHF control={control} name="validFrom" labelKey="console:licenses.fields.valid_from" />
      <JalaliDatePickerRHF
        control={control}
        name="validUntil"
        labelKey="console:licenses.fields.valid_until"
      />
      <TextFieldRHF
        control={control}
        name="minVersion"
        labelKey="console:licenses.fields.min_version"
        inputProps={{ dir: 'ltr' }}
      />
      <TextFieldRHF
        control={control}
        name="maxVersion"
        labelKey="console:licenses.fields.max_version"
        inputProps={{ dir: 'ltr' }}
      />
    </FormDialog>
  );
}

function SigningKeyCard() {
  const { api } = usePlatformSession();
  const { t } = useTranslation('console');
  const key = useQuery({
    queryKey: consoleKeys.licenseSigningKey,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/licenses/signing-key')),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (!key.data) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="license-signing-key">
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('console:licenses.signing_key.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('console:licenses.signing_key.help')}
        </Typography>
        <Typography variant="body2">
          {t('console:licenses.signing_key.key_id')}: <bdi dir="ltr">{key.data.keyId}</bdi>
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => download(`${key.data.keyId}.pub.pem`, key.data.publicKeyPem)}
          data-testid="license-signing-key-download"
        >
          {t('console:licenses.signing_key.download')}
        </Button>
      </Stack>
    </Paper>
  );
}

export function LicensesPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [customerId, setCustomerId] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState<IssuedLicenseView | null>(null);

  const licenses = useQuery({
    queryKey: consoleKeys.licenses(customerId),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/licenses', {
          params: { query: customerId.trim() ? { customerId: customerId.trim() } : {} },
        }),
      ),
  });

  const file = useMutation({
    mutationFn: (licenseId: string) =>
      unwrap(
        api.GET('/api/v1/platform/console/licenses/{licenseId}/file', {
          params: { path: { licenseId } },
        }),
      ),
    onSuccess: (data) => download(data.fileName, data.content),
  });

  const revoke = useMutation({
    mutationFn: (licenseId: string) =>
      unwrap(
        api.POST('/api/v1/platform/console/licenses/{licenseId}:revoke', {
          params: { path: { licenseId } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.licensesRoot });
      setRevoking(null);
    },
  });

  const columns: DataTableColumn<IssuedLicenseView>[] = [
    { id: 'customer', headerKey: 'console:licenses.columns.customer', cell: (row) => row.customerName },
    {
      id: 'installation',
      headerKey: 'console:licenses.columns.installation',
      cell: (row) => <bdi dir="ltr">{row.installationId}</bdi>,
    },
    { id: 'plan', headerKey: 'console:licenses.columns.plan', cell: (row) => row.plan },
    {
      id: 'validity',
      headerKey: 'console:licenses.columns.validity',
      cell: (row) => (
        <>
          <JalaliDate value={row.validFrom} /> – <JalaliDate value={row.validUntil} />
        </>
      ),
    },
    {
      id: 'versions',
      headerKey: 'console:licenses.columns.versions',
      cell: (row) => (
        <bdi dir="ltr">{[row.minVersion ?? '*', row.maxVersion ?? '*'].join(' – ')}</bdi>
      ),
    },
    {
      id: 'status',
      headerKey: 'console:licenses.columns.status',
      cell: (row) => (
        <StatusChip
          tone={row.revokedAt ? 'no_show' : 'active'}
          labelKey={row.revokedAt ? 'console:licenses.revoked' : 'console:licenses.valid'}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'console:licenses.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            onClick={() => file.mutate(row.licenseId)}
            data-testid={`license-download-${row.licenseId}`}
          >
            {t('console:licenses.download')}
          </Button>
          {!row.revokedAt && (
            <Button
              size="small"
              color="error"
              onClick={() => setRevoking(row)}
              data-testid={`license-revoke-${row.licenseId}`}
            >
              {t('console:licenses.revoke')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_CONSOLE_005}
      titleKey="console:licenses.title"
      actions={
        <Button variant="contained" onClick={() => setIssuing(true)} data-testid="license-issue">
          {t('console:licenses.issue.open')}
        </Button>
      }
    >
      <Stack spacing={2}>
        <SigningKeyCard />
        <TextField
          label={t('console:licenses.filter_customer')}
          value={customerId}
          onChange={(event) => setCustomerId(event.target.value)}
          size="small"
          slotProps={{ htmlInput: { dir: 'ltr', maxLength: 64 } }}
          data-testid="license-filter-customer"
        />
        <DataTable
          captionKey="console:licenses.title"
          columns={columns}
          rows={licenses.data}
          getRowId={(row) => row.licenseId}
          isLoading={licenses.isPending}
          error={licenses.error}
          onRetry={() => void licenses.refetch()}
          emptyTitleKey="console:licenses.empty"
        />
      </Stack>
      <IssueDialog open={issuing} onClose={() => setIssuing(false)} />
      {revoking && (
        <ConfirmDialog
          open
          titleKey="console:licenses.revoke_confirm.title"
          messageKey="console:licenses.revoke_confirm.description"
          messageValues={{ customer: revoking.customerName }}
          confirmKey="console:licenses.revoke"
          destructive
          busy={revoke.isPending}
          error={revoke.error}
          onConfirm={() => revoke.mutate(revoking.licenseId)}
          onCancel={() => setRevoking(null)}
        />
      )}
    </ListPage>
  );
}
